import * as THREE from 'three';
import type { Hole } from '../../course/Hole';
import { SurfaceType } from '../../terrain/SurfaceType';
import { isPointInRegion, type Region } from '../../utils/geometry';
import { Units } from '../../utils/Units';
import { CLUBS, expectedCarryMeters, type ClubDefinition } from '../Club';
import type { TrajectoryId } from '../Trajectory';
import type { WindField } from '../WindField';
import type { ShotTypeId } from '../ShotTypes';
import type { AiSkillProfile } from './AiGolfer';

export type AiStrategy =
  'attack-pin' | 'safe-approach' | 'tee-position' | 'layup' | 'recovery' | 'lag-putt';
export interface AiShotPlan {
  shotType: ShotTypeId;
  target: THREE.Vector3;
  club: ClubDefinition;
  trajectory: TrajectoryId;
  power: number;
  shotShape: 'straight' | 'draw' | 'fade';
  aggression: number;
  dispersionRadiusMeters: number;
  strategy: AiStrategy;
  riskScore: number;
  expectedCarryMeters: number;
  hazardPenalties: number;
  intendedRollDistanceMeters?: number;
}
export interface AiShotPlannerInput {
  ballPosition: THREE.Vector3;
  currentLie: SurfaceType;
  hole: Hole;
  wind: WindField;
  skill: AiSkillProfile;
  shotNumber: number;
  elapsedSeconds?: number;
}

export class AiShotPlanner {
  plan(input: AiShotPlannerInput): AiShotPlan {
    const pin = input.hole.activePin.position.clone();
    const distance = input.ballPosition.clone().sub(pin).setY(0).length();
    if (input.currentLie === SurfaceType.Green) return this.putt(input, distance);
    if (input.currentLie === SurfaceType.Bunker)
      return this.shortGamePlan(input, 'bunker', 'sand-wedge', 'normal', distance);
    if (distance < Units.yardsToMeters(20) && input.currentLie === SurfaceType.Fairway)
      return this.shortGamePlan(input, 'chip', 'pitching-wedge', 'normal', distance);
    const recovery =
      input.currentLie === SurfaceType.Rough || input.currentLie === SurfaceType.OutOfBounds;
    const candidates = this.candidateTargets(input, distance, recovery);
    const scored = candidates.map((target) => ({
      target,
      score: this.scoreTarget(input, target),
      risk: this.riskAt(input.hole, target),
    }));
    scored.sort((a, b) => b.score - a.score);
    const selected = scored[0] ?? { target: pin, score: 0, risk: 0.5 };
    const targetDistance = selected.target.clone().sub(input.ballPosition).setY(0).length();
    const club = this.selectClub(targetDistance, input.currentLie, recovery, input.skill);
    const intoWind =
      input.wind
        .sample(input.ballPosition, input.elapsedSeconds ?? 0)
        .normalize()
        .dot(selected.target.clone().sub(input.ballPosition).setY(0).normalize()) < -0.35;
    const trajectory: TrajectoryId =
      recovery || input.currentLie === SurfaceType.Rough
        ? 'low'
        : intoWind
          ? 'low'
          : distance < Units.yardsToMeters(130)
            ? 'high'
            : 'normal';
    const strategy: AiStrategy = recovery
      ? 'recovery'
      : input.hole.par === 5 &&
          input.shotNumber === 2 &&
          selected.target.distanceTo(pin) > Units.yardsToMeters(40)
        ? 'layup'
        : input.shotNumber === 1
          ? 'tee-position'
          : selected.risk < 0.25 && input.skill.aggression > 60
            ? 'attack-pin'
            : 'safe-approach';
    const dispersionRadiusMeters = this.dispersion(
      input.skill,
      club,
      input.currentLie,
      targetDistance,
      selected.risk,
    );
    return {
      shotType: recovery ? 'punch' : 'full',
      target: selected.target,
      club,
      trajectory,
      power: THREE.MathUtils.clamp(
        (targetDistance / Math.max(expectedCarryMeters(club), 1)) * 88,
        42,
        100,
      ),
      shotShape: input.skill.shotShaping > 70 ? 'draw' : 'straight',
      aggression: input.skill.aggression,
      dispersionRadiusMeters,
      strategy,
      riskScore: selected.risk,
      expectedCarryMeters: expectedCarryMeters(club),
      hazardPenalties: selected.risk * 100,
    };
  }

  private putt(input: AiShotPlannerInput, distance: number): AiShotPlan {
    const club = CLUBS.find((c) => c.id === 'putter')!;
    const toCup = input.hole.activePin.position.clone().sub(input.ballPosition).setY(0);
    const direction = toCup.lengthSq() > 0.001 ? toCup.normalize() : new THREE.Vector3(0, 0, 1);
    const leavePastCupMeters =
      distance < Units.yardsToMeters(6) ? 0.46 : distance < Units.yardsToMeters(18) ? 0.65 : 0.15;
    return {
      shotType: 'putt',
      target: input.hole.activePin.position.clone().addScaledVector(direction, leavePastCupMeters),
      club,
      trajectory: 'normal',
      power: THREE.MathUtils.clamp(28 + Units.metersToYards(distance) * 5, 18, 92),
      shotShape: 'straight',
      aggression: input.skill.putting,
      dispersionRadiusMeters: Math.max(
        0.025,
        distance * (1.02 - input.skill.putting / 100) * 0.045,
      ),
      strategy: distance > Units.yardsToMeters(30) ? 'lag-putt' : 'attack-pin',
      riskScore: 0,
      expectedCarryMeters: 0,
      hazardPenalties: 0,
      intendedRollDistanceMeters: Math.max(0.3, distance + leavePastCupMeters),
    };
  }
  private shortGamePlan(
    input: AiShotPlannerInput,
    shotType: ShotTypeId,
    clubId: 'sand-wedge' | 'pitching-wedge',
    trajectory: TrajectoryId,
    distance: number,
  ): AiShotPlan {
    const club = CLUBS.find((c) => c.id === clubId)!;
    return {
      shotType,
      target: input.hole.activePin.position.clone(),
      club,
      trajectory,
      power: THREE.MathUtils.clamp((distance / (shotType === 'chip' ? 30 : 60)) * 100, 8, 100),
      shotShape: 'straight',
      aggression: input.skill.aggression,
      dispersionRadiusMeters: Math.max(
        0.5,
        distance * (1 - input.skill.wedgeAccuracy / 125) * 0.12,
      ),
      strategy: 'attack-pin',
      riskScore: 0,
      expectedCarryMeters: shotType === 'chip' ? distance * 0.45 : distance * 0.8,
      hazardPenalties: 0,
      intendedRollDistanceMeters: shotType === 'chip' ? distance * 0.55 : distance * 0.2,
    };
  }
  private candidateTargets(
    input: AiShotPlannerInput,
    distanceToPin: number,
    recovery: boolean,
  ): THREE.Vector3[] {
    const teeToPin = input.hole.activePin.position
      .clone()
      .sub(input.hole.primaryTee.position)
      .setY(0)
      .normalize();
    const pin = input.hole.activePin.position.clone();
    if (recovery)
      return [
        input.ballPosition
          .clone()
          .addScaledVector(
            pin.clone().sub(input.ballPosition).setY(0).normalize(),
            Units.yardsToMeters(90),
          ),
        pin.clone().addScaledVector(teeToPin, -Units.yardsToMeters(80)),
      ];
    if (input.shotNumber === 1 && distanceToPin > Units.yardsToMeters(250))
      return [
        input.ballPosition.clone().addScaledVector(teeToPin, Units.yardsToMeters(240)),
        input.ballPosition.clone().addScaledVector(teeToPin, Units.yardsToMeters(210)),
        input.ballPosition.clone().addScaledVector(teeToPin, Units.yardsToMeters(180)),
      ];
    if (input.hole.par === 5 && input.shotNumber === 2 && distanceToPin > Units.yardsToMeters(235))
      return [
        pin,
        pin.clone().addScaledVector(teeToPin, -Units.yardsToMeters(95)),
        pin.clone().addScaledVector(teeToPin, -Units.yardsToMeters(115)),
      ];
    return [
      pin,
      this.regionCenter(input.hole.def.green).lerp(pin, 0.35),
      pin.clone().addScaledVector(teeToPin, -Units.yardsToMeters(8)),
    ];
  }
  private scoreTarget(input: AiShotPlannerInput, target: THREE.Vector3): number {
    const pin = input.hole.activePin.position;
    const distPenalty = target.distanceTo(pin) * 0.4;
    const risk = this.riskAt(input.hole, target) * (140 - input.skill.courseManagement);
    return (
      1000 -
      distPenalty -
      risk +
      input.skill.aggression * (target.distanceTo(pin) < Units.yardsToMeters(25) ? 0.8 : 0.15)
    );
  }
  private riskAt(hole: Hole, target: THREE.Vector3): number {
    const p = { x: target.x, z: target.z };
    const inAny = (rs: readonly Region[]) => rs.some((r) => isPointInRegion(p, r));
    let risk = 0;
    if (inAny(hole.def.bunkers)) risk += 0.45;
    if (inAny(hole.def.outOfBounds)) risk += 1;
    if (
      hole.def.waterHazardIds.length &&
      !isPointInRegion(p, hole.def.fairway) &&
      !isPointInRegion(p, hole.def.green)
    )
      risk += 0.25;
    return risk;
  }
  private selectClub(
    distance: number,
    lie: SurfaceType,
    recovery: boolean,
    skill: AiSkillProfile,
  ): ClubDefinition {
    if (recovery && lie !== SurfaceType.Rough) return CLUBS.find((c) => c.id === 'pitching-wedge')!;
    const liePenalty = lie === SurfaceType.Rough ? 0.84 : lie === SurfaceType.Bunker ? 0.66 : 1;
    const maxDriverRisk = skill.aggression > 45;
    const playable = CLUBS.filter((c) => c.id !== 'putter' && (maxDriverRisk || c.id !== 'driver'));
    return playable.reduce(
      (best, club) =>
        Math.abs(expectedCarryMeters(club) * liePenalty - distance) <
        Math.abs(expectedCarryMeters(best) * liePenalty - distance)
          ? club
          : best,
      playable[0],
    );
  }
  private dispersion(
    skill: AiSkillProfile,
    club: ClubDefinition,
    lie: SurfaceType,
    distance: number,
    risk: number,
  ): number {
    const accuracy =
      club.id === 'driver'
        ? skill.drivingAccuracy
        : club.id === 'pitching-wedge'
          ? skill.wedgeAccuracy
          : skill.ironAccuracy;
    const lieMult = lie === SurfaceType.Rough ? 1.35 : lie === SurfaceType.Bunker ? 1.65 : 1;
    return Math.max(
      1.2,
      (distance * 0.075 + club.dispersionTendency * 8) *
        lieMult *
        (1.25 - accuracy / 100) *
        (1.15 - skill.consistency / 250) *
        (1 + risk),
    );
  }
  private regionCenter(region: Region): THREE.Vector3 {
    if (region.kind === 'circle') return new THREE.Vector3(region.center.x, 0, region.center.z);
    const sum = region.points.reduce(
      (v: THREE.Vector3, point) => v.add(new THREE.Vector3(point.x, 0, point.z)),
      new THREE.Vector3(),
    );
    return sum.multiplyScalar(1 / Math.max(1, region.points.length));
  }
}
